/* eslint-disable no-undef */
import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Colors from '../../theme/colors';

import {
  BarChartContainer,
  BarChartTitleTypography,
  BarChartLegend,
  BarChartLegendLabel,
  LegendTypography,
  AxisLabelYTypography,
  DisclosureTypography,
} from './styles';

const checkD3ScriptLoaded = (url, setD3Loaded, attempt) => {
  if (attempt < 10) {
    const scripts = document.getElementsByTagName('script');
    for (let i = scripts.length; i--;) {
      if (scripts[i].src === url && typeof d3 !== 'undefined') {
        setD3Loaded();
        return;
      }
    }
    setTimeout(() => {
      checkD3ScriptLoaded(url, setD3Loaded, attempt + 1);
    }, 500);
  }
};

const BarChartComponent = ({
  axisLabelY,
  displayAsCurrency,
  title,
  yScaleMax,
  yScaleMin,
  yScaleTicks,
  dataSet,
  dataLabels,
  disclosureText,
  loadD3Script,
}) => {
  const [d3Loaded, setD3Loaded] = useState(false);
  const ref = useRef();
  const margin = {
    top: 50,
    bottom: 50,
    left: 50,
    right: 50,
  };
  const height = 500;
  const width = 600;
  const datas = dataSet;
  const dataLegendLabels = dataLabels;
  const gridLabelColor = Colors.neutral.grey54;
  const axisLabelFontSize = '1.3rem';
  const axisLabelFontWeight = '200';
  const fontFamily = 'font-family="Guardian Sans Light"';
  // Feel free to update any colors beyond the first two values. Only light blue and blue were shown in comps from design.
  const colorsArray = [
    Colors.accent.lightBlue,
    Colors.primary.blue,
    Colors.pcg.gold1,
    Colors.cta.yellow,
    Colors.accent.blue,
    Colors.system.darkGreen,
  ];
  const dataColumns = Object.keys(datas[0]).length - 1;

  useEffect(() => {
    const d3Script = 'https://d3js.org/d3.v4.min.js';
    if (
      loadD3Script
      && typeof document !== 'undefined'
      && window.navigator.userAgent.match(/MSIE|Trident/) === null
    ) {
      const script = document.createElement('script');

      script.src = d3Script;
      script.async = true;

      document.body.appendChild(script);
      script.onload = () => {
        setD3Loaded(true);
      };
    } else {
      // Component is not loading D3 which means another bar probably is, let's wait for the script to get on the page then load...
      checkD3ScriptLoaded(
        d3Script,
        () => {
          setD3Loaded(true);
        },
        1,
      );
    }
  }, []);

  useEffect(() => {
    if (d3Loaded && !!d3) {
      // Create the SVG ///////////////////////////////////////////
      const svg = d3
        .select(ref.current)
        .append('svg')
        .attr('height', '100%')
        .attr('width', '100%') // setting svg width to 100%
        .attr('viewBox', [-margin.left, 0, width + 60, height])
        .attr('style', 'overflow: visible;');

      // Creating the color array
      const keys = Object.keys(datas[0]).slice(1);
      const z = d3.scaleOrdinal().range(colorsArray);

      // X AXIS  ////////////////////////////////////////////////////////  X AXIS
      const x0 = d3
        .scaleBand()
        .domain(datas.map(d => d.xValue))
        .range([margin.left, width - margin.right])
        .padding(0.3)
        .rangeRound([0, width])
        .paddingInner(0.2); // Padding around both columns, adjust to alter the column width

      const x1 = d3
        .scaleBand()
        .domain(keys)
        .range([0, x0.bandwidth()]);

      // Setting up x axis (Across the bottom)
      const xAxis = (g) => {
        g.attr('transform', `translate(0,${height})`)
          .call(d3.axisBottom(x0))
          .attr('font-size', axisLabelFontSize)
          .attr('color', gridLabelColor)
          .attr('font-family', fontFamily)
          .attr('font-weight', axisLabelFontWeight);
      };
      svg.append('g').call(xAxis);

      // Y AXIS  //////////////////////////////////////////////////////  Y AXIS
      const y = d3
        .scaleLinear()
        .domain([yScaleMin, yScaleMax])
        .rangeRound([height, 0]);

      // display Y axis values as dollars (No decimals)
      const dollarFormat = d => `$${d}`;

      // Display Y axis values as plain numbers.
      const numericFormat = datas.format;

      // Setting Y Axis label format based on props
      const yAxisFormat = displayAsCurrency ? dollarFormat : numericFormat;

      // Setting up y axis ///////////
      const yAxis = (g) => {
        g.call(
          d3
            .axisLeft(y)
            .tickFormat(yAxisFormat)
            .ticks(yScaleTicks),
        )
          .attr('font-size', axisLabelFontSize)
          .attr('color', gridLabelColor)
          .attr('font-family', fontFamily)
          .attr('font-weight', axisLabelFontWeight)
          .call(g => g.select('.domain').remove())
          .call(g => g.selectAll('.tick line').attr('style', 'display:none'));
      };

      // Y Axis Grid lines  ////////
      const yAxisGrid = (g) => {
        g.call(
          d3
            .axisLeft(y)
            .tickSize(width)
            .ticks(yScaleTicks)
            .tickFormat(''),
        )
          .attr('transform', `translate(${width},0)`)
          .call(g => g.select('.domain').remove())
          .call(g => g.selectAll('.tick line').attr('stroke', gridLabelColor));
      };

      svg.append('g').call(yAxis);
      svg.append('g').call(yAxisGrid);

      // Apply elements to the SVG //////////////////////////////////////////////
      svg
        .append('g')
        .selectAll('g')
        .data(datas)
        .enter()
        .append('g')
        .attr('transform', d => `translate(${x0(d.xValue)},${margin.bottom})`)
        .selectAll('rect')
        .data(d => keys.map(key => ({ key, value: d[key] })))
        .enter()
        .append('rect')
        .classed('nm-bar-chart-column', true) // add custom class to columns
        .attr('style', 'transform: scale(1, -1)') // flips the bars upside down so the animation originates at the bottom
        .attr('x', d => x1(d.key))
        .attr('height', d => 0) // initial bar height 0
        .attr('y', 0 - height + margin.bottom)
        .attr('width', x1.bandwidth())
        .attr('fill', d => z(d.key));

      // FIRE ANIMATION ////////////////
      svg
        .selectAll('.nm-bar-chart-column')
        .transition()
        .duration(800)
        .attr('y', margin.bottom - height)
        .attr('height', d => height - y(d.value))
        .delay((d, i) => i * 100);
    }
  }, [d3Loaded]);

  return (
    <BarChartContainer>
      <BarChartTitleTypography
        component="h6"
        align="center"
        disableBottomPadding>
        {title}
      </BarChartTitleTypography>
      <AxisLabelYTypography>{axisLabelY}</AxisLabelYTypography>
      <div ref={ref} />
      {dataColumns > 1 && (
        <BarChartLegend>
          {dataLegendLabels.map((label, i) => (
            <BarChartLegendLabel
              key={`data-legend-${label}`}
              color={colorsArray[i]}>
              <LegendTypography component="p">{label}</LegendTypography>
            </BarChartLegendLabel>
          ))}
        </BarChartLegend>
      )}
      {disclosureText && (
        <DisclosureTypography component="p" align="center">
          {disclosureText}
        </DisclosureTypography>
      )}
    </BarChartContainer>
  );
};

BarChartComponent.propTypes = {
  title: PropTypes.any,
  dataSet: PropTypes.array.isRequired,
  dataLabels: PropTypes.array.isRequired,
  axisLabelY: PropTypes.string,
  displayAsCurrency: PropTypes.bool,
  yScaleMax: PropTypes.number.isRequired,
  yScaleMin: PropTypes.number,
  yScaleTicks: PropTypes.number.isRequired,
  disclosureText: PropTypes.any,
  loadD3Script: PropTypes.bool,
};

BarChartComponent.defaultProps = {
  displayAsCurrency: true,
  yScaleMax: 7,
  yScaleMin: 0,
  yScaleTicks: 7,
  loadD3Script: true,
};

export default BarChartComponent;
